import { useEffect } from "react";

/**
 * Lightweight per-route SEO manager. Updates document.title, meta tags, and
 * link[rel=canonical] on mount, and cleans them up on unmount. We avoid
 * react-helmet-async so we don't add a dependency for what amounts to a few
 * DOM writes per navigation.
 *
 * Also injects a Schema.org JSON-LD <script> block if `jsonLd` is provided.
 * Blocks are keyed by `data-seo="1"` so this hook never touches meta tags
 * added by the initial index.html payload.
 */

const PROD_HOST = "pixel-empires.com";
const CANONICAL_ORIGIN = `https://${PROD_HOST}`;
const IS_PROD_HOST =
  typeof window !== "undefined" && window.location.hostname === PROD_HOST;

function upsertMeta(selector, attr, value, content) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    el.setAttribute("data-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

export default function useSEO({
  title,
  description,
  path,
  image = "/og-cover.svg",
  type = "website",
  jsonLd = null,
  noindex = false,
} = {}) {
  useEffect(() => {
    if (title) document.title = title;
    const canonical = `${CANONICAL_ORIGIN}${path || (typeof window !== "undefined" ? window.location.pathname : "/")}`;

    if (description) upsertMeta("meta[name='description']", "name", "description", description);
    upsertLink("canonical", canonical);

    // Robots: force noindex on any non-production host (preview URLs, staging,
    // localhost) so search engines don't index the preview in parallel with
    // the production domain. Explicit `noindex` also wins over the base
    // template robots meta.
    const effectiveNoindex = noindex || !IS_PROD_HOST;
    upsertMeta("meta[name='robots']", "name", "robots", effectiveNoindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");

    // Open Graph
    if (title) upsertMeta("meta[property='og:title']", "property", "og:title", title);
    if (description) upsertMeta("meta[property='og:description']", "property", "og:description", description);
    upsertMeta("meta[property='og:url']", "property", "og:url", canonical);
    upsertMeta("meta[property='og:type']", "property", "og:type", type);
    upsertMeta("meta[property='og:site_name']", "property", "og:site_name", "PixelEmpire AI");
    upsertMeta("meta[property='og:image']", "property", "og:image", image.startsWith("http") ? image : `${CANONICAL_ORIGIN}${image}`);

    // Twitter Card
    upsertMeta("meta[name='twitter:card']", "name", "twitter:card", "summary_large_image");
    if (title) upsertMeta("meta[name='twitter:title']", "name", "twitter:title", title);
    if (description) upsertMeta("meta[name='twitter:description']", "name", "twitter:description", description);
    upsertMeta("meta[name='twitter:image']", "name", "twitter:image", image.startsWith("http") ? image : `${CANONICAL_ORIGIN}${image}`);

    // JSON-LD structured data
    // Remove any prior route-scoped JSON-LD, then inject the new one.
    document.head
      .querySelectorAll('script[type="application/ld+json"][data-seo="1"]')
      .forEach((s) => s.remove());
    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((obj) => {
        const s = document.createElement("script");
        s.setAttribute("type", "application/ld+json");
        s.setAttribute("data-seo", "1");
        s.textContent = JSON.stringify(obj);
        document.head.appendChild(s);
      });
    }
  }, [title, description, path, image, type, JSON.stringify(jsonLd), noindex]);
}

/** Prebuilt JSON-LD blocks used across the site. */
export const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "PixelEmpire AI",
  url: "https://pixel-empires.com",
  logo: "https://pixel-empires.com/logo512.png",
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "PixelEmpire AI",
  url: "https://pixel-empires.com",
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: "https://pixel-empires.com/?q={search_term_string}",
    },
    "query-input": "required name=search_term_string",
  },
};

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url.startsWith("http") ? it.url : `https://pixel-empires.com${it.url}`,
    })),
  };
}

export function faqSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((qa) => ({
      "@type": "Question",
      name: qa.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: qa.a,
      },
    })),
  };
}

export const productSchema = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "PixelEmpire Advertising Block",
  description:
    "Permanent 10x10 pixel advertising block on the PixelEmpire AI 100x100 grid. One-time payment, lifetime placement, dofollow SEO backlink.",
  brand: {
    "@type": "Brand",
    name: "PixelEmpire AI",
  },
  image: "https://pixel-empires.com/og-cover.png",
  offers: {
    "@type": "Offer",
    priceCurrency: "USD",
    price: "50.00",
    availability: "https://schema.org/InStock",
    url: "https://pixel-empires.com/pricing",
  },
};
